import React, { useState } from 'react';
import { X, CheckCircle, AlertCircle, RefreshCw, FileText } from 'lucide-react';

const ProgressModal = ({ 
  isOpen, 
  onClose, 
  progress, 
  isComplete, 
  hasError, 
  errorMessage,
  title = "Atualizando Histórico",
  canClose = false 
}) => {
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  if (!isOpen) return null;

  const getStatusIcon = () => {
    if (hasError) {
      return <AlertCircle className="w-8 h-8 text-red-500" />;
    }
    if (isComplete) {
      return <CheckCircle className="w-8 h-8 text-green-500" />;
    }
    return <RefreshCw className="w-8 h-8 text-blue-500 animate-spin" />;
  };

  const getStatusText = () => {
    if (hasError) {
      return "Erro na atualização";
    }
    if (isComplete) {
      return "Atualização concluída!";
    }
    return "Processando...";
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9997]">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          {canClose && (
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Status Icon */}
          <div className="flex justify-center mb-4">
            {getStatusIcon()}
          </div>

          {/* Status Text */}
          <div className="text-center mb-6">
            <p className="text-lg font-medium text-gray-900 mb-2">
              {getStatusText()}
            </p>
            {progress.currentFranquia && (
              <>
                <p className="text-sm text-gray-600">
                  Processando: {progress.currentFranquia}
                </p>
                {progress.step && (
                  <p className="text-xs text-gray-500 mt-1">
                    {progress.step}
                  </p>
                )}
              </>
            )}
          </div>

          {/* Progress Bar */}
          <div className="mb-4">
            <div className="flex justify-between text-sm text-gray-600 mb-2">
              <span>Progresso</span>
              <span>{progress.current || 0} / {progress.total || 0}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className={`h-2 rounded-full transition-all duration-300 ${
                  hasError ? 'bg-red-500' : 
                  isComplete ? 'bg-green-500' : 
                  'bg-blue-500'
                }`}
                style={{ 
                  width: `${progress.total > 0 ? (progress.current / progress.total) * 100 : 0}%` 
                }}
              />
            </div>
            <div className="text-center mt-2 text-sm text-gray-600">
              {progress.total > 0 ? Math.round((progress.current / progress.total) * 100) : 0}%
            </div>
          </div>

          {/* Statistics */}
          {(progress.sucessos > 0 || progress.erros > 0) && (
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <div className="grid grid-cols-2 gap-4 text-center">
                <div>
                  <div className="text-2xl font-bold text-green-600">
                    {progress.sucessos || 0}
                  </div>
                  <div className="text-sm text-gray-600">Sucessos</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-red-600">
                    {progress.erros || 0}
                  </div>
                  <div className="text-sm text-gray-600">Erros</div>
                </div>
              </div>
            </div>
          )}

          {/* Error Message */}
          {hasError && errorMessage && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-red-700">{errorMessage}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end space-x-3">
            {(isComplete || hasError) && (
              <>
                {/* Botão Detalhes - só aparece quando há erros */}
                {(progress.erros > 0 && progress.detalhesErros && progress.detalhesErros.length > 0) && (
                  <button
                    onClick={() => setShowDetailsModal(true)}
                    className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors flex items-center"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Detalhes
                  </button>
                )}
                
                <button
                  onClick={onClose}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Fechar
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Modal de Detalhes dos Erros */}
      {showDetailsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9998]">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[80vh] overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b">
              <h3 className="text-lg font-semibold text-gray-900">Detalhes dos Erros</h3>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              <div className="mb-4">
                <p className="text-sm text-gray-600">
                  Foram encontrados {progress.erros} erro(s) durante a atualização:
                </p>
              </div>

              {/* Lista de Erros */}
              <div className="space-y-4">
                {progress.detalhesErros && progress.detalhesErros.length > 0 ? (
                  progress.detalhesErros.map((detalhe, index) => (
                    <div key={index} className="border border-red-200 rounded-lg p-4 bg-red-50">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center">
                          <AlertCircle className="w-5 h-5 text-red-500 mr-2 flex-shrink-0" />
                          <h4 className="font-medium text-gray-900">
                            {detalhe.franquia?.nome || 'Franquia não identificada'}
                          </h4>
                        </div>
                        <span className="text-xs text-gray-500">
                          {new Date(detalhe.timestamp).toLocaleString('pt-BR')}
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                        {detalhe.franquia?.id && (
                          <div>
                            <span className="font-medium text-gray-700">ID:</span>
                            <span className="ml-2 text-gray-600">{detalhe.franquia.id}</span>
                          </div>
                        )}
                        {detalhe.franquia?.cnpj && (
                          <div>
                            <span className="font-medium text-gray-700">CNPJ:</span>
                            <span className="ml-2 text-gray-600">{detalhe.franquia.cnpj}</span>
                          </div>
                        )}
                        {detalhe.franquia?.host && (
                          <div className="md:col-span-2">
                            <span className="font-medium text-gray-700">Host:</span>
                            <span className="ml-2 text-gray-600 break-all">{detalhe.franquia.host}</span>
                          </div>
                        )}
                      </div>
                      
                      <div className="mt-3 pt-3 border-t border-red-200">
                        <span className="font-medium text-gray-700">Erro:</span>
                        <p className="mt-1 text-sm text-red-700 bg-red-100 p-2 rounded">
                          {detalhe.erro}
                        </p>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <AlertCircle className="mx-auto h-12 w-12 text-gray-400 mb-3" />
                    <h3 className="text-sm font-medium text-gray-900">Nenhum detalhe disponível</h3>
                    <p className="text-sm text-gray-500 mt-1">
                      Os detalhes dos erros não foram disponibilizados.
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end p-6 border-t">
              <button
                onClick={() => setShowDetailsModal(false)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProgressModal;
